import { useQuery } from '@apollo/client/react'
import { useMemo } from 'react'
import { useMembershipRole } from 'lib/hooks/useMembershipRole/useMembershipRole'
import { useBusinessTraits } from 'lib/hooks/useBusinessTraits/useBusinessTraits'
import { useAccountingPlatform } from 'lib/hooks/useAccountingPlatform/useAccountingPlatform'
import { getProfileTraits } from 'lib/helpers/getProfileTraits/getProfileTraits'
import { getBusinessSizeBucket } from 'lib/helpers/getBusinessSizeBucket/getBusinessSizeBucket'
import { getAccountAgeDays } from 'lib/helpers/getAccountAgeDays/getAccountAgeDays'
import { isImpersonationSession } from 'lib/helpers/isImpersonationSession/isImpersonationSession'
import { DEFAULT_DISPLAY_TIME_ZONE } from 'lib/helpers/resolveTimeZone/resolveTimeZone'
import { useActiveTimeZone, useDeviceTimeZone } from 'lib/hooks/useActiveTimeZone/useActiveTimeZone'
import { CountersHookDocument } from 'lib/hooks/useCounters/useCounters.graphql'
import { ProfileHookDocument } from 'lib/hooks/gql/useProfileHook.graphql'

export const useUserTraits = () => {
  const { data: countersData } = useQuery(CountersHookDocument)
  const { data: profileData } = useQuery(ProfileHookDocument)
  const profile = profileData?.profile
  const membershipRole = useMembershipRole()
  const impersonationSession = isImpersonationSession()
  const businessTraits = useBusinessTraits()
  const accountingPlatform = useAccountingPlatform()
  const deviceTimeZone = useDeviceTimeZone()
  const activeTimeZone = useActiveTimeZone()

  return useMemo(
    () => ({
      impersonationSession,
      membershipRole,
      ...businessTraits,
      ...(profile && getProfileTraits(profile)),
      ...(countersData && {
        payableInvoicesCount: countersData.counters.all,
        receivableInvoicesCount: countersData.counters.receivableInvoices,
      }),
      ...(accountingPlatform && {
        accountingPlatformReadOnly: accountingPlatform.isReadOnly,
        accountingPlatformSyncAutomatically: accountingPlatform.syncAutomatically,
      }),
      // we track authProvider separately, not as a part of getProfileTraits
      // to avoid issue in useAcceptVendorInvitation when accept mutation is crashed
      ...(profile && { ...getProfileTraits(profile), authProvider: profile.authProvider }),
      userId: profile?.id ?? null,
      userRole: membershipRole?.toLowerCase() ?? null,
      businessTier: businessTraits?.businessPlan?.toLowerCase() ?? null,
      businessSizeBucket: getBusinessSizeBucket(countersData?.counters.all),
      billsCreated: countersData?.counters.all ?? null,
      signupDate: profile?.createdAt ?? null,
      accountAgeDays: getAccountAgeDays(profile?.createdAt),
      timezone: activeTimeZone ?? DEFAULT_DISPLAY_TIME_ZONE,
      deviceTimezone: deviceTimeZone ?? null,
    }),
    [
      profile,
      accountingPlatform,
      countersData,
      membershipRole,
      impersonationSession,
      businessTraits,
      deviceTimeZone,
      activeTimeZone,
    ],
  )
}
